import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ShopFilters from '@/components/feature/ShopFilters';
import { gins, Product } from '@/mocks/products';

const BADGE_KEYS: Record<string, string> = {
  'Mais Vendido': 'badge_best_seller',
  'Novo': 'badge_new',
  'Premium': 'badge_premium',
  'Cask Strength': 'badge_cask_strength',
  'Clássico': 'badge_classic',
  'Exclusivo': 'badge_exclusive',
  'Edição Limitada': 'badge_limited',
  'Navy Strength': 'badge_navy',
  'Importado': 'badge_imported',
};

const DESC_EN: Record<string, string> = {
  'botanica-london-dry': 'Classic juniper, Sicilian lemon peel and coriander. The perfect London gin.',
  'floral-botanical-gin': 'Rose, lavender, hibiscus and violet petals. A floral gin of rare delicacy.',
  'small-batch-spiced-gin': 'Pink pepper, cardamom, ginger and juniper. Bold, spiced and memorable.',
  'mediterranean-dry-gin': 'Seville orange, thyme, rosemary and juniper. The Mediterranean in a bottle.',
  'navy-strength-gin': 'Power and character. Intense juniper, spices and naval strength in every sip.',
  'japanese-yuzu-gin': 'Yuzu, green tea, sakura and juniper. Japanese delicacy in gin form.',
};

const STYLE_EN: Record<string, string> = {
  'London Dry': 'London Dry',
  'Floral': 'Floral',
  'Especiado': 'Spiced',
  'Cítrico': 'Citrus',
};

const ORIGIN_EN: Record<string, string> = {
  'Londres': 'London',
  'Escócia': 'Scotland',
  'Irlanda': 'Ireland',
  'Espanha': 'Spain',
  'Inglaterra': 'England',
  'Japão': 'Japan',
};

const GinCard = ({ product }: { product: Product }) => {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language?.startsWith('en');
  const displayBadge = product.badge ? (BADGE_KEYS[product.badge] ? t(BADGE_KEYS[product.badge], product.badge) : product.badge) : null;
  const displayDesc = isEn ? (DESC_EN[product.slug] ?? product.description) : product.description;
  const displayStyle = product.style ? (isEn ? (STYLE_EN[product.style] ?? product.style) : product.style) : null;

  return (
    <div className="group relative bg-[#111111] border border-white/8 rounded-xl overflow-hidden hover:border-[#c9a84c]/40 transition-all duration-500" data-product-shop>
      {displayBadge && (
        <div className="absolute top-4 left-4 z-10 bg-[#c9a84c] text-black text-[10px] tracking-widest uppercase font-bold px-3 py-1 rounded-full whitespace-nowrap">
          {displayBadge}
        </div>
      )}
      {displayStyle && (
        <div className="absolute top-4 right-4 z-10 bg-black/60 backdrop-blur-sm text-white/60 text-[10px] tracking-widest uppercase px-3 py-1 rounded-full border border-white/10 whitespace-nowrap">
          {displayStyle}
        </div>
      )}
      <Link to={`/produto/${product.slug}`} className="block">
        <div className="relative h-64 bg-gradient-to-b from-[#1a1a1a] to-[#0d0d0d] flex items-center justify-center overflow-hidden">
          <img
            src={product.image}
            alt={product.name}
            className="h-56 w-auto object-contain object-top transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-transparent to-transparent opacity-60" />
        </div>
      </Link>
      <div className="p-5">
        <p className="text-[#c9a84c]/70 text-[10px] tracking-[0.25em] uppercase mb-1 font-medium">
          {isEn ? (ORIGIN_EN[product.region ?? ''] ?? product.origin) : product.origin} · {product.abv}
        </p>
        <Link to={`/produto/${product.slug}`}>
          <h3 className="text-white text-lg font-light mb-1 group-hover:text-[#c9a84c] transition-colors" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {product.name}
          </h3>
        </Link>
        <p className="text-white/40 text-xs mb-4 leading-relaxed line-clamp-2">{displayDesc}</p>
        <div className="flex items-center justify-between">
          <p className="text-[#c9a84c] text-xl font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {product.priceFormatted}
          </p>
          <Link
            to={`/produto/${product.slug}`}
            className="bg-[#c9a84c] hover:bg-[#b8963e] text-black text-[10px] tracking-widest uppercase font-bold px-5 py-2.5 rounded-md transition-all duration-300 whitespace-nowrap"
          >
            {t('shop_buy', 'Comprar')}
          </Link>
        </div>
      </div>
    </div>
  );
};

const GinsPage = () => {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language?.startsWith('en');
  const [sort, setSort] = useState('featured');
  const [style, setStyle] = useState('all');
  const [origin, setOrigin] = useState('all');

  const SORT_OPTIONS = [
    { value: 'featured', label: t('shop_featured', 'Destaques') },
    { value: 'price-asc', label: t('shop_price_asc', 'Menor Preço') },
    { value: 'price-desc', label: t('shop_price_desc', 'Maior Preço') },
    { value: 'name-asc', label: t('shop_name_az', 'A–Z') },
  ];

  const STYLE_OPTIONS = [
    { value: 'all', label: t('gins_style_all', 'Todos') },
    { value: 'London Dry', label: 'London Dry' },
    { value: 'Floral', label: isEn ? 'Floral' : 'Floral' },
    { value: 'Especiado', label: isEn ? 'Spiced' : 'Especiado' },
    { value: 'Cítrico', label: isEn ? 'Citrus' : 'Cítrico' },
  ];

  const ORIGIN_OPTIONS = [
    { value: 'all', label: t('gins_origin_all', 'Todas') },
    { value: 'Londres', label: isEn ? 'London' : 'Londres' },
    { value: 'Escócia', label: isEn ? 'Scotland' : 'Escócia' },
    { value: 'Irlanda', label: isEn ? 'Ireland' : 'Irlanda' },
    { value: 'Espanha', label: isEn ? 'Spain' : 'Espanha' },
    { value: 'Inglaterra', label: isEn ? 'England' : 'Inglaterra' },
    { value: 'Japão', label: isEn ? 'Japan' : 'Japão' },
  ];

  const STYLE_STRIPS = [
    { name: 'London Dry', label: 'London Dry', icon: 'ri-goblet-line', desc: isEn ? 'Classic' : 'Clássico' },
    { name: 'Floral', label: isEn ? 'Floral' : 'Floral', icon: 'ri-flower-line', desc: isEn ? 'Delicate' : 'Delicado' },
    { name: 'Especiado', label: isEn ? 'Spiced' : 'Especiado', icon: 'ri-star-line', desc: isEn ? 'Intense' : 'Intenso' },
    { name: 'Cítrico', label: isEn ? 'Citrus' : 'Cítrico', icon: 'ri-sun-line', desc: isEn ? 'Refreshing' : 'Refrescante' },
  ];

  const filtered = useMemo(() => {
    let list = [...gins];
    if (style !== 'all') list = list.filter(p => p.style === style);
    if (origin !== 'all') list = list.filter(p => p.region === origin);
    if (sort === 'price-asc') list.sort((a, b) => a.price - b.price);
    else if (sort === 'price-desc') list.sort((a, b) => b.price - a.price);
    else if (sort === 'name-asc') list.sort((a, b) => a.name.localeCompare(b.name));
    else list.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    return list;
  }, [sort, style, origin]);

  return (
    <>
      <Navbar />
      <main className="bg-[#0a0a0a] min-h-screen pt-[72px]">
        {/* Hero */}
        <div className="relative h-72 md:h-80 overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: "url('https://readdy.ai/api/search-image?query=premium%20gin%20bottles%20collection%20botanical%20ingredients%20herbs%20citrus%20flowers%20elegant%20dark%20bar%20setting%20luxury%20spirits%20atmospheric%20moody%20photography%20professional&width=1600&height=500&seq=gin-page-hero&orientation=landscape')" }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/65 to-black/40" />
          <div className="relative z-10 h-full flex flex-col items-start justify-center px-8 md:px-16 max-w-3xl">
            <p className="text-[#c9a84c] text-xs tracking-[0.3em] uppercase mb-3 font-medium">
              {t('gins_page_label', 'Categoria Premium')}
            </p>
            <h1 className="text-5xl md:text-6xl font-light text-white mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {t('gins_page_title', 'Gins')}
            </h1>
            <p className="text-white/50 text-sm font-light max-w-md">
              {t('gins_page_subtitle')}
            </p>
            <div className="flex items-center gap-2 mt-5 text-xs text-white/40 tracking-widest uppercase">
              <Link to="/" className="hover:text-[#c9a84c] transition-colors">{t('nav_home', 'Home')}</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/loja" className="hover:text-[#c9a84c] transition-colors">{t('nav_shop', 'Loja')}</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-white/60">{t('nav_gins', 'Gins')}</span>
            </div>
          </div>
        </div>

        {/* Style quick nav */}
        <div className="border-b border-white/8 bg-[#0d0d0d]">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex flex-wrap gap-3">
            {STYLE_STRIPS.map(s => (
              <button
                key={s.name}
                onClick={() => setStyle(style === s.name ? 'all' : s.name)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-xs tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap ${
                  style === s.name
                    ? 'bg-[#c9a84c]/10 border-[#c9a84c]/50 text-[#c9a84c]'
                    : 'border-white/10 text-white/50 hover:border-white/20 hover:text-white/70'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${s.icon} text-sm`}></i>
                </div>
                {s.label}
                <span className="text-[10px] opacity-60 hidden sm:inline">· {s.desc}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-10 py-14">
          <ShopFilters
            sortOptions={SORT_OPTIONS}
            sortValue={sort}
            onSortChange={setSort}
            filterGroups={[
              {
                label: t('gins_origin_label', 'Origem'),
                key: 'origin',
                options: ORIGIN_OPTIONS,
                value: origin,
                onChange: setOrigin,
              },
            ]}
            totalCount={filtered.length}
          />

          {filtered.length === 0 ? (
            <div className="text-center py-24">
              <i className="ri-search-line text-4xl text-white/20 mb-4 block"></i>
              <p className="text-white/40 text-sm">{t('gins_page_empty')}</p>
              <button
                onClick={() => { setStyle('all'); setOrigin('all'); }}
                className="mt-4 text-[#c9a84c] text-xs tracking-widest uppercase cursor-pointer hover:underline"
              >
                {t('gins_page_clear')}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map(p => <GinCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
};

export default GinsPage;
